import { Step } from "@stepperize/react";
import { Stepper } from "@stepperize/react";
import dayjs from "dayjs";
import { steps } from "./utils";
import { z } from "zod";

export const EVENT_MODE_PUNCHUP = "punchup";

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const hexColorRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export enum STEPS_IDS {
  SHOW_DETAILS = "showDetails",
  TICKET_TYPES = "ticketTypes",
  LINEUP = "lineup",
  SEAT_CHARTS = "seatCharts",
  REVIEW = "review",
}

export const showDetailsSchema = z
  .object({
    mode: z.enum([EVENT_MODE_PUNCHUP]).default(EVENT_MODE_PUNCHUP),
    name: z.string().min(3),
    description: z.string().optional(),
    start_date: z.string().regex(dateRegex),
    // Update to use z.string().time() when upgrading zod to >= 3.25.X
    start_time: z.string().regex(timeRegex),
    end_date: z.string().regex(dateRegex),
    // Update to use z.string().time() when upgrading zod to >= 3.25.X
    end_time: z.string().regex(timeRegex),
    venue: z.string().min(1),
    sale_date: z.date().optional(),
    doors_open: z.string(),
    gate_time: z.string().optional(),
    location: z.string().min(1),
    city: z.string().optional(),
    state: z.string().optional(),
    time_zone: z.string().min(1, "Timezone can not be empty"),
    support: z.string().optional(),
    postal_code: z.string().optional(),
    ticketing_mode: z.enum(["dynamic", "static"]).default("static"),
    quantity: z.string().optional(),
    approval_required: z.boolean().default(false),
    // Admin-only: create the show as inactive — hidden from every public surface and
    // unsellable, visible only in the admin panels. Carried to the Tixologi event on create.
    inactive: z.boolean().default(false),
    has_multiple_dates: z.boolean().default(false),
    dates: z.array(
      z
        .object({
          start_date: z.date(),
          end_date: z.date(),
        })
        .optional()
    ),
    tags: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
          description: z.string().optional(),
          tag_type: z.enum(["event", "ticket_type"]).optional(),
        })
      )
      .max(10, "Maximum 10 tags allowed")
      .optional(),
    image_url: z.string().optional(),
    password: z
      .union([
        z.string().min(8, "Password must be at least 8 characters"),
        z.undefined(),
      ])
      .optional(),
    hidden_until: z
      .union([
        z.string().regex(dateRegex, "Please select a valid date"),
        z.undefined(),
      ])
      .optional(),
    ticket_delivery_hours_before_event: z
      .number()
      .int()
      .min(0, "Hours must be at least 0")
      .max(72, "Hours cannot exceed 72 (3 days)")
      .optional()
      .nullable(),
    banner_image_url: z.string().optional().nullable(),
    banner_link_url: z
      .union([
        z.string().url("Please enter a valid URL (e.g. https://example.com)"),
        z.literal(""),
        z.undefined(),
        z.null(),
      ])
      .optional()
      .nullable(),
  })
  .refine(
    ({ start_date, end_date, has_multiple_dates }) => {
      if (!has_multiple_dates && dayjs(start_date).isAfter(dayjs(end_date))) {
        return false;
      }
      return true;
    },
    {
      path: ["end_date"],
      message: "End date cannot be before start date ",
    }
  )
  .refine(
    ({ start_date, end_date, start_time, end_time, has_multiple_dates }) => {
      if (has_multiple_dates) {
        return true;
      }

      if (
        !dateRegex.test(start_date) ||
        !timeRegex.test(start_time) ||
        !dateRegex.test(end_date) ||
        !timeRegex.test(end_time)
      ) {
        return false;
      }
      const startDateTime = dayjs(start_date)
        .hour(parseInt(start_time.split(":")[0]))
        .minute(parseInt(start_time.split(":")[1]));
      const endDateTime = dayjs(end_date)
        .hour(parseInt(end_time.split(":")[0]))
        .minute(parseInt(end_time.split(":")[1]));

      return endDateTime.isAfter(startDateTime);
    },
    {
      path: ["end_time"],
      message: "End time must be after start time",
    }
  )
  .refine(
    ({ hidden_until }) => {
      // If hidden_until is set to an empty string, it's invalid
      if (hidden_until === "") {
        return false;
      }
      return true;
    },
    {
      path: ["hidden_until"],
      message: "Hidden until date is required when enabled",
    }
  )
  .refine(
    ({
      start_date,
      start_time,
      time_zone,
      ticket_delivery_hours_before_event,
    }) => {
      // Only skip validation when no delay is set (null/undefined). 0 is a
      // valid value (deliver at event start) and must still be validated —
      // e.g. it's invalid for an event that has already started.
      if (
        ticket_delivery_hours_before_event === null ||
        ticket_delivery_hours_before_event === undefined
      ) {
        return true;
      }

      // Check if we have all required fields for validation
      if (!start_date || !start_time || !time_zone) {
        return true; // Let other validations handle missing fields
      }

      // Calculate event start time in the event's timezone
      const eventStartDateTime = dayjs.tz(
        `${start_date} ${start_time}`,
        "YYYY-MM-DD HH:mm",
        time_zone
      );

      // Calculate hours until event
      const now = dayjs();
      const hoursUntilEvent = eventStartDateTime.diff(now, "hours", true);

      // Delay cannot be longer than time until event
      return ticket_delivery_hours_before_event <= hoursUntilEvent;
    },
    {
      path: ["ticket_delivery_hours_before_event"],
      message:
        "Ticket delivery delay cannot be longer than the time until the event starts",
    }
  )
  .refine(
    ({ banner_image_url, banner_link_url }) => {
      // A banner link with no image cannot be rendered in the email
      if (banner_link_url) {
        return !!banner_image_url;
      }
      return true;
    },
    {
      path: ["banner_link_url"],
      message: "Upload a banner image to attach a link",
    }
  );

export const ticketTypesSchema = z.array(
  z
    .object({
      name: z.string().min(1),
      description: z.string().optional(),
      initial_price: z.coerce.number().min(0).step(0.01),
      fixed_fee: z.coerce.number().min(0).step(0.01).optional().nullable(),
      percentage_fee: z.coerce
        .number()
        .min(0)
        .max(1)
        .step(0.0001)
        .optional()
        .nullable(),
      final_price: z.coerce.number().min(0).step(0.01).optional().nullable(),
      pay_what_you_want: z.boolean().default(false),
      min_price: z.coerce.number().min(0).step(0.01).optional().nullable(),
      max_price: z.coerce.number().min(0).step(0.01).optional().nullable(),
      amount: z.number({ invalid_type_error: "Amount is required" }).min(0),
      selling_date: z.string(),
      custom_selling_datetime: z.string().optional().nullable(),
      off_selling_date: z.string(),
      custom_off_selling_datetime: z.string().optional().nullable(),
      ticket_visibility: z.boolean().default(true),
      tickets_pool_idx: z.coerce.number().optional().nullable(),
      bundle_size: z.number().min(0).optional().nullable(),
      seats_category_key: z.string().optional().nullable(),
      tags: z
        .array(
          z.object({
            id: z.number(),
            name: z.string(),
            description: z.string().optional(),
            tag_type: z.enum(["event", "ticket_type"]).optional(),
          })
        )
        .max(10, "Maximum 10 tags allowed")
        .optional(),
      color: z
        .string()
        .regex(
          hexColorRegex,
          "Color must be a valid hex code (e.g., #FFF or #FFFFFF)"
        )
        .optional()
        .nullable(),
      color_name: z.string().optional().nullable(),
    })
    .refine(
      ({ bundle_size, amount }) => {
        if (bundle_size && amount < bundle_size) {
          return false;
        }
        return true;
      },
      {
        path: ["amount"],
        message:
          "Ticket quantity must be greater than or equal to the bundle size",
      }
    )
    .refine(
      ({ pay_what_you_want, initial_price }) => {
        if (
          !pay_what_you_want &&
          (initial_price === null || initial_price === undefined)
        ) {
          return false;
        }
        return true;
      },
      {
        path: ["initial_price"],
        message: "Ticket price is required",
      }
    )
    .refine(
      ({ pay_what_you_want, min_price }) => {
        if (
          pay_what_you_want &&
          (min_price === null || min_price === undefined)
        ) {
          return false;
        }
        return true;
      },
      {
        path: ["min_price"],
        message: "Minimum price is required",
      }
    )
    .refine(
      ({ pay_what_you_want, max_price }) => {
        if (
          pay_what_you_want &&
          (max_price === null || max_price === undefined)
        ) {
          return false;
        }
        return true;
      },
      {
        path: ["max_price"],
        message: "Maximum price is required",
      }
    )
    .refine(
      ({ pay_what_you_want, min_price, max_price }) => {
        if (
          pay_what_you_want &&
          min_price !== null &&
          min_price !== undefined &&
          max_price !== null &&
          max_price !== undefined &&
          min_price >= max_price
        ) {
          return false;
        }
        return true;
      },
      {
        path: ["max_price"],
        message: "Maximum price must be greater than minimum price",
      }
    )
    .refine(
      ({ selling_date, custom_selling_datetime }) => {
        if (selling_date === "custom") {
          return !!custom_selling_datetime;
        }
        return true;
      },
      {
        path: ["custom_selling_datetime"],
        message: "Custom on sale date is required",
      }
    )
    .refine(
      ({ off_selling_date, custom_off_selling_datetime }) => {
        if (off_selling_date === "custom") {
          return !!custom_off_selling_datetime;
        }
        return true;
      },
      {
        path: ["custom_off_selling_datetime"],
        message: "Custom off sale date is required",
      }
    )
    .refine(
      ({
        selling_date,
        custom_selling_datetime,
        off_selling_date,
        custom_off_selling_datetime,
      }) => {
        if (
          selling_date === "custom" &&
          custom_selling_datetime &&
          off_selling_date === "custom" &&
          custom_off_selling_datetime
        ) {
          const sellingDateTime = dayjs(custom_selling_datetime);
          const offSellingDateTime = dayjs(custom_off_selling_datetime);
          return offSellingDateTime.isAfter(sellingDateTime);
        }
        return true;
      },
      {
        path: ["custom_off_selling_datetime"],
        message: "Ticket sales end date must be after the on sale date",
      }
    )
);

export const lineupSchema = z.array(z.string()).default([]);

export const seatChartsSchema = z.object({
  selectedChartKey: z.string().optional(),
  selectedChartId: z.number().optional(),
});

export const eventCreationFormSchema = z.object({
  venue_id: z.string().optional(),
  ticketing_mode: z.enum(["internal", "external"]).default("internal"),
  [STEPS_IDS.SHOW_DETAILS]: showDetailsSchema,
  [STEPS_IDS.TICKET_TYPES]: ticketTypesSchema,
  [STEPS_IDS.LINEUP]: lineupSchema,
  [STEPS_IDS.SEAT_CHARTS]: seatChartsSchema,
  pools: z.array(
    z.object({
      name: z.string().min(1),
      total_amount: z.number().min(0),
    })
  ),
});

export type EventCreationForm = z.infer<typeof eventCreationFormSchema>;

export type TicketTypeFormType = z.infer<typeof ticketTypesSchema>;

export type EventMode = typeof EVENT_MODE_PUNCHUP;

export interface ImageBackType {
  url: string;
  base64: string;
  name: string;
}

export interface StepperButtonProps {
  stepper: Stepper<typeof steps>;
  step: Step;
  index: number;
  array: Step[];
  currentIndex: number;
}
